<template>
  <div v-if="isVisible" class="audiogram-modal" @click.self="close">
    <div class="audiogram-content">
      <h2 class="text-xl font-bold mb-4">Audiogramm</h2>
      
      
      <!-- Add Testgeräusch Lautstärke dropdown -->
      <div class="mb-4">
        <label for="testgeraeuschLautstaerke" class="block text-sm font-medium text-gray-700">Testgeräusch Lautstärke</label>
        <select
          id="testgeraeuschLautstaerke"
          v-model="testgeraeuschLautstaerke"
          class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          @change="updateTestgeraeuschLautstaerke"
        >
          <option v-for="value in testgeraeuschLautstaerkeOptions" :key="value" :value="value">
            {{ value }}
          </option>
        </select>
      </div>

      <svg :width="width + 100" :height="height + 100" @click="handleClick">
        <g :transform="`translate(70, 20)`">
          <!-- Raster -->
          <line v-for="(x, index) in xLines" :key="'x'+index" 
                :x1="x" :y1="0" :x2="x" :y2="height" stroke="#ff0000" stroke-width="1" />
          <line v-for="(y, index) in yLines" :key="'y'+index" 
                :x1="0" :y1="y" :x2="width" :y2="y" stroke="#ff0000" stroke-width="1" />
          
          <!-- X-Achse Beschriftung -->
          <text v-for="(label, index) in xLabels" :key="'xlabel'+index"
                :x="xLines[index]" :y="height + 20" text-anchor="middle" font-size="12" fill="black">
            {{ label }}
          </text>
          <text :x="width / 2" :y="height + 40" text-anchor="middle" font-size="14" fill="black">Frequenz in kHz</text>
          
          <!-- Y-Achse Beschriftung -->
          <text v-for="(label, index) in yLabels" :key="'ylabel'+index"
                x="-10" :y="yLines[index]" text-anchor="end" alignment-baseline="middle" font-size="12" fill="black">
            {{ label }}
          </text>
          <text :transform="yAxisLabelTransform" text-anchor="middle" font-size="14" fill="black">Hörverlust in dB</text>
          
         <!-- Linie -->
         <polyline :points="linePoints" fill="none" stroke="red" stroke-width="2" />
          
          <!-- Punkte -->
          <circle v-for="point in points" :key="point.x+','+point.y"
                  :cx="scaleX(point.x)" :cy="scaleY(point.y)" r="4" fill="red" stroke="white" stroke-width="2" />
        </g>
      </svg>

      <!-- Add Weber Test checkbox -->
      <div class="mb-4">
        <label class="flex items-center">
          <input
            v-model="weberTestErgebnis"
            type="checkbox"
            class="mr-2"
            @change="updateWeberTest"
          />
          Weber bei 500 Hz
        </label>
      </div>

      <button @click="close" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
        Schließen
      </button>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useERezeptStore } from '../../stores/eRezeptStore';

export default {
  props: {
    side: {
      type: String,
      required: true,
      validator: (value) => ['rechts', 'links'].includes(value),
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 600
    },
    height: {
      type: Number,
      default: 400
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const eRezeptStore = useERezeptStore();

    const xLabels = ['0,125', '0,25', '0,5', '1', '1,5', '2', '3', '4', '6', '8', '10'];
    const yLabels = ['-10', '0', '10', '20', '30', '40', '50', '60', '70', '80', '90', '100', '110'];

    const rawPoints = computed(() => eRezeptStore.getAudiogramPoints(props.side));
    const sortedPoints = computed(() => [...rawPoints.value].sort((a, b) => a.x - b.x));

    const points = ref([]);

    watch(sortedPoints, (newPoints) => {
      points.value = newPoints;
    }, { immediate: true });

    const xLines = computed(() => xLabels.map((_, i) => scaleX(i)));
    const yLines = computed(() => yLabels.map((_, i) => scaleY(parseInt(yLabels[i]))));

    const linePoints = computed(() => points.value
      .map(p => `${scaleX(p.x)},${scaleY(p.y)}`)
      .join(' '));

    const scaleX = (x) => x * (props.width / (xLabels.length - 1));
    const scaleY = (y) => props.height - ((y + 10) * props.height / 120);

    const yAxisLabelTransform = computed(() => `rotate(-90) translate(-${props.height / 2}, -50)`);

    const handleClick = (event) => {
      const svg = event.currentTarget;
      const rect = svg.getBoundingClientRect();
      const x = event.clientX - rect.left - 70;
      const y = event.clientY - rect.top - 20;

      if (x < 0 || x > props.width || y < 0 || y > props.height) {
        return;
      }

      const xStep = props.width / (xLabels.length - 1);
      const yStep = props.height / 12;

      const xIndex = Math.round(x / xStep);
      const yValue = Math.round((props.height - y) / yStep) * 10 - 10;

      if (yValue >= -10 && yValue <= 110 && xIndex >= 0 && xIndex < xLabels.length) {
        eRezeptStore.updateAudiogramPoint(props.side, xIndex, yValue);
      }
    };

    const weberTestErgebnis = ref(eRezeptStore.muster15formData.ohren[props.side].weberTestErgebnis);
    const testgeraeuschLautstaerke = ref(eRezeptStore.muster15formData.ohren[props.side].testgeraeuschLautstaerke);
    const testgeraeuschLautstaerkeOptions = computed(() => Array.from({length: 10}, (_, i) => i * 10));

    const updateWeberTest = () => {
      eRezeptStore.muster15formData.ohren[props.side].weberTestErgebnis = weberTestErgebnis.value;
    };

    const updateTestgeraeuschLautstaerke = () => {
      eRezeptStore.updateMuster15OhrField(props.side, 'testgeraeuschLautstaerke', null, testgeraeuschLautstaerke.value);
    };

    const close = () => {
      emit('close');
    };

    return {
      xLabels,
      yLabels,
      xLines,
      yLines,
      linePoints,
      points,
      handleClick,
      scaleX,
      scaleY,
      close,
      yAxisLabelTransform,
      weberTestErgebnis,
      testgeraeuschLautstaerke,
      testgeraeuschLautstaerkeOptions,
      updateWeberTest,
      updateTestgeraeuschLautstaerke
    };
  }
};
</script>

<style scoped>
.audiogram-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.audiogram-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 80%;
  max-height: 80%;
  overflow: auto;
}

.close-button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.audiogram {
  margin: 20px 0;
}
</style>